"use client"

import { type FormEvent, useMemo, useState } from "react"
import { Button } from "~/components/legacy/common/button"
import { Input } from "~/components/legacy/common/input"
import { sitemapRowsToCsv, sitemapDiagnosticsToCsv } from "~/lib/sitemap-export"
import { useTrackEvent } from "~/hooks/use-track-event"
import type { SitemapResult } from "~/server/sitemap-explorer/types"

function download(filename: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }))
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export function SitemapExplorer() {
  const trackEvent = useTrackEvent()
  const [url, setUrl] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<SitemapResult | null>(null)
  const [query, setQuery] = useState("")
  const [source, setSource] = useState("")
  const rows = useMemo(
    () =>
      result?.rows.filter(
        row =>
          (!source || row.sourceSitemap === source) &&
          (!query || row.url.toLowerCase().includes(query.toLowerCase())),
      ) ?? [],
    [result, query, source],
  )
  const sources = useMemo(
    () => [...new Set(result?.rows.map(row => row.sourceSitemap) ?? [])],
    [result],
  )
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)
    setError(null)
    setResult(null)
    setQuery("")
    setSource("")
    trackEvent("sitemap.analysis_started")
    try {
      const response = await fetch("/api/sitemap-explorer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
        signal: AbortSignal.timeout(35000),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || "Could not explore this site.")
      setResult(body)
      trackEvent("sitemap.analysis_completed", {
        url_count: body.rows.length,
        issue_count: body.diagnostics?.length ?? 0,
        partial: body.truncated || body.analysisComplete === false,
      })
    } catch (error) {
      setError(error instanceof Error ? error.message : "Could not explore this site.")
      trackEvent("sitemap.analysis_failed")
    } finally {
      setLoading(false)
    }
  }
  return (
    <div className="grid gap-6">
      <form onSubmit={submit} className="grid gap-3 rounded-lg border bg-card p-5">
        <label htmlFor="sitemap-url" className="text-sm font-medium">
          Domain, robots.txt or sitemap URL
        </label>
        <div className="flex flex-wrap gap-3">
          <Input
            id="sitemap-url"
            value={url}
            onChange={event => setUrl(event.target.value)}
            placeholder="example.com or https://example.com/sitemap.xml"
            required
            maxLength={2048}
            disabled={loading}
            className="min-w-60 flex-1"
          />
          <Button type="submit" disabled={loading}>
            {loading ? "Exploring…" : "Explore sitemaps"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Explore public sitemaps, follow nested indexes and deduplicate up to 5,000 URLs. Page
          content is not crawled.
        </p>
      </form>
      {error && (
        <p
          role="alert"
          className="rounded-md border border-destructive/30 p-4 text-sm text-destructive"
        >
          {error}
        </p>
      )}
      {loading && (
        <p role="status" className="text-sm text-muted-foreground">
          Discovering sitemaps and collecting URLs. This can take up to 25 seconds.
        </p>
      )}
      {result && (
        <section aria-label="Sitemap results" className="grid gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold">
                {rows.length.toLocaleString("en")} unique {rows.length === 1 ? "URL" : "URLs"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {query || source ? `${result.rows.length.toLocaleString("en")} collected · ` : ""}
                {result.documents.length} documents read
                {result.truncated
                  ? " · limit reached; partial results"
                  : result.analysisComplete === false
                    ? " · some documents failed; partial results"
                    : ""}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  download("sitemap-urls.csv", sitemapRowsToCsv(rows), "text/csv;charset=utf-8")
                  trackEvent("sitemap.exported", { format: "csv", row_count: rows.length })
                }}
              >
                Download CSV
              </Button>
              <Button
                variant="secondary"
                onClick={() =>
                  download(
                    "sitemap-urls.json",
                    JSON.stringify(
                      {
                        ...result,
                        rows,
                        filters: { query, sourceSitemap: source || null },
                        totalCollected: result.rows.length,
                      },
                      null,
                      2,
                    ),
                    "application/json",
                  )
                }
              >
                Download JSON
              </Button>
            </div>
          </div>
          <section
            aria-label="Sitemap diagnostics"
            className="grid gap-3 rounded-lg border bg-card p-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-lg font-semibold">Sitemap diagnostics</h3>
              {!!result.diagnostics?.length && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    download(
                      "sitemap-issues.csv",
                      sitemapDiagnosticsToCsv(result.diagnostics || []),
                      "text/csv;charset=utf-8",
                    )
                    trackEvent("sitemap.diagnostics_exported", {
                      issue_count: result.diagnostics?.length ?? 0,
                    })
                  }}
                >
                  Download issues CSV
                </Button>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {result.diagnosticSummary?.duplicates ?? 0} duplicate URLs removed ·{" "}
              {result.diagnosticSummary?.crossHostUrls ?? 0} URLs on other hosts. Page HTTP status
              and indexing eligibility are not checked.
            </p>
            {result.diagnostics?.length ? (
              <ul className="grid gap-3 text-sm">
                {result.diagnostics.map(issue => (
                  <li
                    key={`${issue.code}-${issue.sourceSitemap}`}
                    className="min-w-0 rounded-md border p-3"
                  >
                    <p className="font-medium">
                      {issue.message}
                      {issue.count > 1 ? ` (${issue.count} occurrences)` : ""}
                    </p>
                    <p className="mt-1 break-all text-xs text-muted-foreground">
                      {issue.sourceSitemap}
                    </p>
                    {issue.url && (
                      <p className="mt-1 break-all text-xs text-muted-foreground">
                        Example: {issue.url}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm">No diagnostic issues found in the documents read.</p>
            )}
            {(result.truncated ||
              result.analysisComplete === false ||
              !!result.diagnosticSummary?.omitted) && (
              <p className="text-sm text-amber-700 dark:text-amber-300">
                This is a partial analysis.
                {result.diagnosticSummary?.omitted
                  ? ` ${result.diagnosticSummary.omitted} additional issue groups were omitted.`
                  : ""}
              </p>
            )}
          </section>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="sitemap-filter" className="mb-1 block text-sm font-medium">
                Find a URL
              </label>
              <Input
                id="sitemap-filter"
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Filter collected URLs"
              />
            </div>
            <div>
              <label htmlFor="sitemap-source" className="mb-1 block text-sm font-medium">
                Source sitemap
              </label>
              <select
                id="sitemap-source"
                value={source}
                onChange={event => setSource(event.target.value)}
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="">All sitemaps</option>
                {sources.map(value => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {(query || source) && (
            <p className="text-xs text-muted-foreground">
              Downloads include the filtered URLs.{" "}
              <button
                type="button"
                onClick={() => {
                  setQuery("")
                  setSource("")
                }}
                className="underline"
              >
                Clear filters
              </button>
            </p>
          )}
          {result.warnings.length > 0 && (
            <details className="rounded-md border p-3">
              <summary className="cursor-pointer text-sm">{result.warnings.length} notices</summary>
              <ul className="mt-2 grid gap-1 text-xs text-muted-foreground">
                {result.warnings.map((warning, index) => (
                  <li key={`${index}-${warning}`}>{warning}</li>
                ))}
              </ul>
            </details>
          )}
          <div
            role="region"
            aria-label="Sitemap URL table, scroll horizontally"
            tabIndex={0}
            className="overflow-x-auto rounded-md border outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="p-3">URL</th>
                  <th className="p-3">Last modified</th>
                  <th className="p-3">Source sitemap</th>
                  <th className="p-3">Frequency</th>
                  <th className="p-3">Priority</th>
                  <th className="p-3">Depth</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 100).map(row => (
                  <tr key={row.url} className="border-t">
                    <td className="max-w-lg break-all p-3">
                      <a
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        {row.url}
                      </a>
                    </td>
                    <td className="whitespace-nowrap p-3">{row.lastmod ?? "Not provided"}</td>
                    <td className="max-w-sm break-all p-3 text-muted-foreground">
                      {row.sourceSitemap}
                    </td>
                    <td className="p-3">{row.changefreq ?? "—"}</td>
                    <td className="p-3">{row.priority ?? "—"}</td>
                    <td className="p-3">{row.depth ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 100 && (
            <p className="text-xs text-muted-foreground">
              Showing the first 100 matching rows. Downloads include every matching URL.
            </p>
          )}
        </section>
      )}
    </div>
  )
}
